import {useEffect, useRef} from "react";
import {cacheData, cacheFetch, convertToCSV, funcName, funcOrValOr, get_dur, getAgo, getDateTech} from "../tools/CommonJS";
import {query} from "../tools/CommonHtml";
import {arrowPlacementClasses, componentInit, useDataSingleton, useStateEx} from "../tools/CommonReact";
import {initSocket} from "../tools/socket";
import {enumOutClickModes} from "../tools/UIPopPanel";

export const isAWS = process.env.REACT_APP_TARGET?.toLowerCase() === "aws" || undefined;
export const isDev = process.env.REACT_APP_ENV?.toLowerCase().startsWith("dev") || undefined;
export const isAwsDev = process.env.REACT_APP_ENV?.toLowerCase() === "devaws" || undefined;
export const isTalDemo = window.location.host === "taldemo.dev.us.cloud.gytpol.com" || undefined;

export const conLog = (...args) => isDev && console.log(`${new Date().toLocaleTimeString().toLocaleLowerCase()} ${
    get_dur(performance.now(), {
        trimLeftZero: 1, addUnit: 1, showMs: 1})} ${funcName(3, {nonNull: 1})}:`, ...args);

/* dev vs AWS:
install:
aws cli:
    url: https://d-906769fca3.awsapps.com/start
    region: us-east-1
kubectl: curl.exe -LO "https://dl.k8s.io/release/v1.28.3/bin/windows/amd64/kubectl.exe"
choco install kubernetes-cli
https://gytpol.perimeter81.com/

aws configure sso
aws sso login --profile Dev-ReadOnly-508648162200
aws eks update-kubeconfig --name dev-us-east-2-gytpol --region us-east-2
assume.bat
kubectl get ns
kubectl port-forward -n kube-system svc/dimadev-analyzer-api-service 9090:8083 -n dimadev
 */


export const appCtx = {
    socket: initSocket(isAWS || isAwsDev)
};

export const msgBox = msg => appCtx.setMsg(msg);
export const isMsgBoxOn = key => appCtx.isPanelOn(key);

// {show, rAnchor, align, margin, transition, children}
export const setTooltip = prop => appCtx.tooltip.set(old => ({...old, ...prop}));
export const isTooltipOn = () => appCtx.tooltip.isShown();

export const showTooltip = (rAnchor, tip, opts = {}) => {
    clearTimeout(appCtx.tooltip.tmHide);
    if (rAnchor)
        setTooltip({show: true, rAnchor, transition: true, children: tip, align: "below left", ...opts});
};

export const hideTooltip = () => {
    clearTimeout(appCtx.tooltip.tmHide);
    appCtx.tooltip.tmHide = setTimeout(() => setTooltip({show: false}), 300);
};

export const genTooltip = (tip, side) => !tip? {} : {
    onMouseEnter: e => showTooltip(
        e.target,
        <div className={`after:border-b-popup bg-popup ${arrowPlacementClasses[side || "left"]}`}>{tip}</div>,
        {margin: 10, outClickMode: enumOutClickModes.propagate}),
    onMouseLeave: hideTooltip
};

const makeAuthHeader = async () =>
    isAWS ?
        {Authorization: `Bearer ${await appCtx.aws.getIdJwtToken()}`}
        :
        isAwsDev?
            {Authorization: `Bearer eyJraWQiOiJkZG1nN2k1NmJUUU9yZjVpcmxjdkQ0XC9lN3lNaTlCaHExVVI3cnlMcFwvTjQ9IiwiYWxnIjoiUlMyNTYifQ.eyJzdWIiOiIzNmFlMTcxMy04ZDUzLTQ3YmYtODA5Ni05MDA3OGU1ZDBmMTIiLCJlbWFpbF92ZXJpZmllZCI6dHJ1ZSwiaXNzIjoiaHR0cHM6XC9cL2NvZ25pdG8taWRwLnVzLWVhc3QtMi5hbWF6b25hd3MuY29tXC91cy1lYXN0LTJfNEVmQmR0WTZEIiwiY29nbml0bzp1c2VybmFtZSI6ImRpbWFAZ3l0cG9sLmNvbSIsIm9yaWdpbl9qdGkiOiI2MzY0NjkwYi1lZGY5LTRmYjYtYTJkZS1jMjE4NWQ5NWY2YWIiLCJhdWQiOiI3amNvYmJwc2pwczMxY2w1dDZ2cm9yY2NldCIsImV2ZW50X2lkIjoiOTg4MmVhOGYtNTcxMS00YmY1LWI1ZTMtMTk3ZGVlMDY3MmNiIiwidG9rZW5fdXNlIjoiaWQiLCJhdXRoX3RpbWUiOjE2OTc5NTkyODMsImV4cCI6MTY5ODA0NzU4OCwiaWF0IjoxNjk4MDQzOTg4LCJqdGkiOiJjYzE0OTQzZC0zM2Q5LTRjYjctYTA4Mi0yNzkyYjUzNTAyZDQiLCJlbWFpbCI6ImRpbWFAZ3l0cG9sLmNvbSJ9.h-94xmpTINYznUQp1W7kTn6GNhhsbvttFZ4D5bVLHQeYlsGGcDjjdQbEFAYlazaZkQTUc3wXc97SczNhgiB8RTckm_-TBI7OAb6c9Q0ut_jlVr_wcO_GkIfPTRQlMquIYEmKcEcEjSFyZESYmMLHitX6YRmhsGDrwjX9NXR-3acKdkRqYuzld5C_FFtrbBAsg_dJzqomGYKWZqag_H4Fs4se4F5q3TE1cBB1xvsbOQ2sOicvH9olaMU10QaAfL7yneGiSSJT4sqadXjnRZTYEyR3oJa83ju415qwcX_nwwRcWN5R_pftAoyIQJQZu18oCibY3JTYxsqx5VTZvsHMIw`}
            :
            {secid: sessionStorage.getItem('secid')};

export function queryAuth({params = {}, onRsp, ...props}) {
    return query({
        params,
        ...props,
        headers: makeAuthHeader,
        onRsp: rsp => {
            const secid = rsp.headers.get("secid");
            if (secid)
                sessionStorage.setItem('secid', secid);         // save user id key for reuse in this session.
            return onRsp?.(rsp);
        }
    });
}

let prevErrBoxId;

export function queryOrErr({api, params, rsp, alt, onLoading, keepLoading, ignoreErr, ...props}) {
    onLoading?.(true);
    return queryAuth({api, params, alt, onJson: r => {
            !keepLoading?.(r) && onLoading?.(false);
            if (!ignoreErr && r.error)
                return !isMsgBoxOn(prevErrBoxId) && (prevErrBoxId = msgBox(
                    <div data-test="error-msgbox" className="flex flex-col gap-4">
                        <div data-test="error-msgbox-text" className="text-base text-sec">{r.error.toString?.()}</div>
                        {!!r.errText && <div className="text-sm">{r.errText}</div>}
                    </div>
                ));
            rsp?.(r);
        }, ...props});
}

// Reuse a singular fetch data throughout the session. Any component using this with the api (serving as key)
// will get the most recent fetch, and refresh when any caller refreshes.
//
export function useFetchDataSingleton({api, socket, resetOnLastUnuse, ...rest}) {
    const {val: rsp, refetch: refresh} = useDataSingleton({
        key: api,
        fetcher: onFetch => queryOrErr({api, ...rest, rsp: r => onFetch(r)}),
        onCtor: refetch => socket && appCtx.socket?.on(socket, refetch),
        onDtor: refetch => socket && appCtx.socket?.off(socket, refetch),
        resetOnLastUnuse
    });

    return {rsp, refresh};
}

/**
 * Data fetch handler. Automatically canceled if component dies while fetch is performed.
 *  @returns {fetchData({})}: Allows to perform any query anywhere in the component, and handles the cleanup if component is destroyed.
 *  @param api string - API url.
 *  @param params object?: parameters to pass to API.
 *  @param paramsCache object?: if provided, will be used for cache matching. Otherwise cache will use normal params.
 *  @param cacheMins number of minutes to use cache without refetching query. If true, use default of 10 mins. If -1, ignore cache, but store result.
 *  @param onData function(rspObj): called on fetched data or init if given. if onData returns a value, it replaces the returned rspObj value.
 *  @param dataSkipCache function(rspObj)?: optional callback to check if response is invalid for cache and for passing to onData.
 *  forwarded parameters to queryOrErr: alt, onLoading, setMsg
 */
export function useFetchDataCb() {
    const vals = useRef({objQs: {}}).current;

    function fetchData({api, params, paramsCache, cacheMins, onData, dataSkipCache, ...rest}) {
        if (vals.destroyed) return;         // in case any debounce triggers.
        const cacheKey = cacheMins && [api, paramsCache ?? params];
        if (cacheMins > 0) {
            cacheMins = typeof cacheMins === "number"? cacheMins : 10;
            const cached = cacheFetch(cacheKey);
            if (cached) {
                conLog(`session cached ${getAgo(cached.created)}: jKey: ${JSON.stringify(cacheKey)}`);
                const isOld = Date.now() - cached.created > cacheMins *60*1000;         // if cached query's age is over cacheMins
                if (isOld) cached.data.calcState = 1;           // Let caller know that refetch is being performed in the background
                onData?.(cached.data);
                if (!isOld) return;                             // if age within cacheMins, don't refetch
            }
        }
        vals.objQs[api]?.cancel();
        vals.objQs[api] = queryOrErr({
            api, params,
            rsp: r => {
                if (cacheMins && !dataSkipCache?.(r))
                    cacheData({
                        key: cacheKey, data: r, created: r?.cacheTime && new Date(r.cacheTime)});
                onData?.(r);
            },
            ...rest
        });
    }

    useEffect(() => {
        vals.destroyed = false;         // useEffect destruct and init run on every hot-reload after editing.
        return () => {
            Object.values(vals.objQs).forEach(q => q.cancel());
            vals.destroyed = true;
        };
    }, []);

    return {fetchData, objQs: vals.objQs};
}


/**
 * Data fetch handler. Automatically canceled if component dies while in fetch or re-fetch is performed.
 *  @param api string - API url.
 *  @param params object|function?: parameters to pass to API.
 *  @param socket string|boolean?: if specified, query will ask the backed to use cached data and apply socket when data refreshes. if true, socket will be api.replace("/", "_").
 *  @param cacheMins number of minutes to use cache without refetching query. if true, use default of 10 mins.
 *  @param deps array?: if specified, will refetch if any item in dependencies array changes.
 *  @param init object?: initial response before query returns. Useful for showing a placeholder on page load. If onData, passed thru onData.
 *  @param onData function(rspObj, isInit)? called on fetched data or init if given. if onData returns a value, it replaces the returned rspObj value.
 *  @param noAutoFetch if true, data won't be fetched immediately, but rather only after requested via the returned fetch func.
 *  @param keepWhileRefetching if true, previously fetched data won't be disposed, until replaced with the new fetch results.
 *  forwarded parameters to queryOrErr: alt, onLoading, setMsg
 *  @param alt object|function?: alternate response if api is 404 (good for dev before backend implements API)
 *  @param onLoading function(isLoading)? called (true) when issuing fetch, and again (false) when fetch is done.
 *  @returns {object} an object with 3 values:
 *   rsp: fetch result, or given alt if fetch 404, or given init before fetch returned. All 3 are passed thru onData if given.
 *   isInit: bool: true if init passed and before response returned
 *   refresh(): callback to async refetch data.
 *   fetch(paramsAdd?): callback to async fetch data, with optional additional overriding params.
 */
export function useFetchData({api, params, socket, cacheMins, deps, init, onData, noAutoFetch, keepWhileRefetching, ...rest}) {
    const clearData = () => ({data: !init? null : onData? onData(init, true) : init, isInit: !!init});
    const {state: st, setState, setStateInRender} = useStateEx(clearData);
    const {fetchData, objQs} = useFetchDataCb();
    const pollFetch = () => fetchDataEx(-1);
    Object.assign(st, {api, params, onData, rest});

    if (componentInit(st, [api, ...deps ?? []], "_init_useEffect")) {    // useEffect doesn't support variable list of deps.
        st.depOrd = (st.depOrd ?? 0) + 1;
        if (!keepWhileRefetching)
            setStateInRender(clearData());
    }

    // useEffect destruct and init run on every hot-reload after editing! https://github.com/facebook/create-react-app/issues/7227
    // so if fetch didn't bring data after first run, you'll have to reload component to retry.
    useEffect(() => {
        if (socket) {
            st.socket = typeof socket === "string"? socket :
                `${api.replace("/", "_")}_${Math.random() * 100000 |0}`;       // random to overcome same api with different params.
            appCtx.socket?.on(st.socket, pollFetch);
        }

        if (!noAutoFetch)
            fetchDataEx();

        return () => {
            clearTimeout(st.tmPoll);
            st.socket && appCtx.socket?.off(st.socket, pollFetch);   // if socket was on - disengage it to stop catching the old socket with its params.
        };
    }, [st.depOrd]);         // eslint-disable-line

    function fetchDataEx(recalc = 0, paramsAdd) {
        const paramsCache = {...funcOrValOr(st.params), ...paramsAdd};
        clearTimeout(st.tmPoll);
        fetchData({
            api: st.api, paramsCache,
            params: {...paramsCache, ...socket && {socket: st.socket, recalc: recalc >= 0? recalc : 0}},
            cacheMins: !recalc? cacheMins : cacheMins && -1,
            dataSkipCache: d => !d || (socket && d.calcState === 2),
            keepLoading: d => socket && d?.calcState >= 1,
            onData: d => {
                if (d.calcState >= 1)
                    st.tmPoll = setTimeout(pollFetch, 60*1000);           // poll every minute. don't trust socket :(
                if (d.calcState !== 2)
                    setState({data: st.onData? st.onData(d, false) : d, isInit: false, noSrvCache: undefined});
                else
                    setState({noSrvCache: true});
            },
            ...st.rest
        });
    }

    return {
        rsp: st.data,
        isInit: st.isInit,
        refresh: (recalc = 1, paramsAdd) => fetchDataEx(recalc, paramsAdd),
        fetch: paramsAdd => {
            if (!keepWhileRefetching)
                setState(clearData());
            fetchDataEx(0, paramsAdd)
        },
        isFetching: () => objQs[st.api]?.isActive,
        cancelFetch: () => objQs[st.api]?.cancel(),
        noSrvCache: st.noSrvCache
    };
}


/**
 * Wrapper around useFetchData to handle paging for a virtual table. Fetches 100 items each time.
 * silentClear(): purges current results should not return until a new fetch is performed.
 */
export function useVirtualPaging({params, deps, debounceMs = 100, ...props}) {
    const clearData = () => ({rsp: null, rows: null, from: 0});
    const {state: st, setState} = useStateEx({...clearData(), rspVer: 0, pageLen: 100});
    const silentClear = () => (Object.assign(st, clearData()), st.rspVer++);

    if (componentInit(st, [props.api, ...deps ?? []]))
        silentClear();

    const {fetch: orgFetch, isFetching, cancelFetch} = useFetchData({
        ...props, deps, params: () => ({...funcOrValOr(params), from: st.from, len: st.pageLen}), onData: r => {
            // process fetched rows into existing vals.rows array
            r = props.onData? props.onData(r) : r;
            st.rows = st.rows ?? [];
            if (!st.from)
                st.rows.length = r.rowCount;
            st.rows.splice(st.from, r.data.length, ...r.data);    // replace/append the new rows
            st.rspVer++;
            return st.rsp = {...r, data: st.rows};
        },
    });

    const onPaging = ({startIndex, fRowsFit, overscanRowCount}) => {
        const fit = (fRowsFit+0.5|0) + overscanRowCount;
        if (!st.rows || startIndex >= st.rows.length ||
            (st.rows[startIndex] !== undefined &&
                st.rows[Math.min(st.rows.length -1 , startIndex + fit -1)] !== undefined))
            return;
        const nextBlank = st.rows.slice(startIndex, startIndex+fit).findIndex(r => r === undefined);
        if (nextBlank === -1) return;

        st.from = startIndex + nextBlank;
        st.pageLen = Math.max(st.pageLen, fit);
        cancelFetch();
        clearTimeout(st.tmDebounce);
        const isRecent = Date.now() - st.lastFetchAt < debounceMs;
        st.lastFetchAt = Date.now();
        if (!isRecent)
            orgFetch();
        else
            st.tmDebounce = setTimeout(orgFetch, debounceMs);
    };

    useEffect(() => () => clearTimeout(st.tmDebounce), []);

    function fetch(paramsAdd) {
        silentClear();
        orgFetch(paramsAdd);
    }

    return {rsp: st.rsp, rspVer: st.rspVer, fetch, onPaging, silentClear, isFetching};
}


export const nameExport = (name, ext) => `${name} ${getDateTech()}${ext ?? ".csv"}`.replace(/[/\\?%*:|"<>]/g, '_');

export function downloadFile({api, params, acceptTypes}) {
    queryOrErr({
        api, params,
        onRsp: rsp => {
            if (acceptTypes?
                !acceptTypes.includes(rsp?.headers.get("content-type")) :
                rsp?.headers.get("content-type").startsWith("application/json"))
                return false;
            return rsp.blob().then(blob => {
                const a = document.createElement('a');
                a.href = window.URL.createObjectURL(blob);
                a.download = params.exportName;
                document.body.appendChild(a); // Required for this to work in FireFox
                a.click();
                a.remove();
            });
        }
    });
}

export const downloadFileFromData = (data, filename) => {
    const a = document.createElement('a');
    a.href = ('data:text/csv;charset=utf-8,%EF%BB%BF' +encodeURIComponent(convertToCSV(data)));
    a.download = nameExport(filename);
    document.body.appendChild(a); // Required for this to work in FireFox
    a.click();
    a.remove();
}
